import { FC, useEffect, useState } from 'react';
import { css, cx } from '@emotion/css';
import tabletWithInterior from './assets/tablet-with-interior.webp';
import tablet from './assets/tablet.webp';
import { UploadArea } from './UploadArea';
import { useTranslation } from 'react-i18next';
import { ProductType } from '@/models/ad';
import { QRCodeSVG } from 'qrcode.react';

type Props = {
  productType: ProductType;
  qrUrl?: string;
  className?: string;
  qrText?: string;
  onUpload?: (file: File) => void;
  accept?: string;
  disabled?: boolean;
  disabledMessage?: string;
  mediaUrl?: string;
};

const Background = () => (
  <>
    <img src={tablet} alt="" className="w-full block lg:hidden" />
    <img src={tabletWithInterior} alt="" className="w-full hidden lg:block" />
  </>
);

export const Tablet: FC<Props> = ({
  onUpload,
  productType,
  mediaUrl,
  qrUrl,
  qrText,
  disabled,
  disabledMessage,
}) => {
  const [preview, setPreview] = useState<string | undefined>();
  const { t } = useTranslation();

  const handlePreview = (url: string) => {
    setPreview(url);
  };

  useEffect(() => {
    setPreview(undefined);
  }, [productType]);

  useEffect(() => {
    setPreview(mediaUrl);
  }, [mediaUrl]);

  const acceptMediaType = getAcceptMediaType(productType);

  return (
    <div className="relative w-full overflow-hidden rounded-xl">
      <Background />

      {disabled && (
        <div className="absolute z-20 inset-0 bg-black bg-opacity-30 backdrop-blur-md flex items-center justify-center">
          <h1 className="text-white text-4xl font-bold w-3/4 text-center">
            {disabledMessage || t('media_section.label.not_available')}
          </h1>
        </div>
      )}

      <div
        className={cx(
          'absolute z-1 overflow-hidden',
          css`
            top: 12.3%;
            left: 8.2%;
            width: 83.5%;
            height: 75.2%;

            @media (min-width: 1024px) {
              top: 24.3%;
              left: 26.6%;
              width: 46.8%;
              height: 51.4%;
            }
          `
        )}
      >
        <section className="rounded-xl lg:rounded-md box-border border-2 relative border-zinc-400 h-full bg-black">
          {onUpload && acceptMediaType && (
            <div className="absolute w-full h-full z-10">
              <UploadArea
                onUpload={onUpload}
                onPreview={handlePreview}
                acceptType={acceptMediaType}
                productType={productType}
                preview={preview}
              />
            </div>
          )}

          {preview &&
            (productType === ProductType.Image ||
              productType === ProductType.CustomImage) && (
              <div className="w-full h-full relative overflow-hidden rounded-xs">
                <img
                  src={preview}
                  className="object-cover w-full h-full absolute top-0 left-0"
                />
              </div>
            )}

          {preview &&
            (productType === ProductType.Video15s ||
              productType === ProductType.Video30s ||
              productType === ProductType.CustomVideo) && (
              <video
                data-testid="video-preview"
                controls
                className="object-contain w-full h-full"
                loop
                muted
                autoPlay
              >
                <source src={preview} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}

          {qrUrl && (
            <div
              className={cx(
                'absolute bg-white',
                css`
                  width: 12%;
                  right: 3%;
                  bottom: 5%;
                  z-index: 1;
                  border-radius: 3px;
                  overflow: hidden;
                `
              )}
            >
              {qrText && (
                <div
                  className={cx(
                    'uppercase',
                    css`
                      font-size: 6px;
                      padding: 3px 5px 0px 5px;
                    `
                  )}
                >
                  {qrText}
                </div>
              )}
              <div className="p-1">
                <QRCodeSVG value={qrUrl} size={60} />
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const getAcceptMediaType = (value?: string) => {
  switch (value) {
    case ProductType.Image:
      return 'image';
    case ProductType.Video15s:
    case ProductType.Video30s:
      return 'video';
    default:
      return;
  }
};
